import { useState, useEffect } from 'react';
import { Button, Card } from '@safetyhub/ui';
import { useAuthStore } from '../../stores/auth';
import { groupsApi, Group } from '../../lib/groups';
import { canReceiveSiteTransfer } from '../../lib/membershipAccess';
import {
  getTransferByToken,
  acceptTransfer,
  rejectTransfer,
  SiteTransferInfo,
} from '../../lib/site-transfer';
import { extractApiError } from '../../lib/api';
import { useToastStore } from '../../stores/toast';
import Modal from '../Modal';

interface SiteTransferAcceptModalProps {
  isOpen: boolean;
  onClose: () => void;
  token: string;
}

export default function SiteTransferAcceptModal({ isOpen, onClose, token }: SiteTransferAcceptModalProps) {
  const { user, isAuthenticated } = useAuthStore();
  const { showToast } = useToastStore();

  const [transferInfo, setTransferInfo] = useState<SiteTransferInfo | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [loadError, setLoadError] = useState('');

  const [selectedOrgId, setSelectedOrgId] = useState('');
  const [selectedGroupId, setSelectedGroupId] = useState('');
  const [groups, setGroups] = useState<Group[]>([]);
  const [isLoadingGroups, setIsLoadingGroups] = useState(false);

  const [isAccepting, setIsAccepting] = useState(false);
  const [isRejecting, setIsRejecting] = useState(false);
  const [actionError, setActionError] = useState('');
  const [isDone, setIsDone] = useState(false);
  const [doneMessage, setDoneMessage] = useState('');

  // 모달이 열릴 때마다 상태 초기화 후 이관 정보 로드
  useEffect(() => {
    if (!isOpen || !token) return;

    setTransferInfo(null);
    setLoadError('');
    setSelectedOrgId('');
    setSelectedGroupId('');
    setGroups([]);
    setActionError('');
    setIsDone(false);
    setDoneMessage('');

    let cancelled = false;

    const loadTransferInfo = async () => {
      setIsLoading(true);
      try {
        const info = await getTransferByToken(token);
        if (!cancelled) setTransferInfo(info);
      } catch (err) {
        if (!cancelled) {
          const apiError = extractApiError(err);
          setLoadError(apiError.message || '이관 정보를 불러올 수 없습니다.');
        }
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    };

    void loadTransferInfo();

    return () => {
      cancelled = true;
    };
  }, [isOpen, token]);

  // 선택한 조직의 그룹 목록 로드
  useEffect(() => {
    if (!selectedOrgId) {
      setGroups([]);
      setSelectedGroupId('');
      return;
    }

    setIsLoadingGroups(true);
    groupsApi
      .findAll(selectedOrgId)
      .then((data) => {
        setGroups(data);
        if (data.length === 1) {
          setSelectedGroupId(data[0].id);
        } else {
          const defaultGroup = data.find((g) => g.isDefault);
          setSelectedGroupId(defaultGroup?.id ?? data[0]?.id ?? '');
        }
      })
      .catch(() => setGroups([]))
      .finally(() => setIsLoadingGroups(false));
  }, [selectedOrgId]);

  const isRequester = isAuthenticated && transferInfo && user?.id === transferInfo.requestedByUserId;

  // 이관받을 조직 후보 — P6-b: 옛 `role === 'OWNER'` → 권한 **key**(`site.transfer`).
  // 서버 `assertCanTransfer`가 받는 조직에 대해 보는 것과 같은 축이다(그 함수 doc).
  const ownerMemberships = (user?.memberships ?? []).filter(canReceiveSiteTransfer);

  const handleAccept = async () => {
    if (!token) return;
    if (!selectedOrgId) {
      setActionError('이관 받을 조직을 선택해주세요.');
      return;
    }

    setActionError('');
    setIsAccepting(true);
    try {
      await acceptTransfer(token, {
        toOrganizationId: selectedOrgId,
        toGroupId: selectedGroupId || undefined,
      });
      setIsDone(true);
      setDoneMessage('이관을 수락했습니다. 현장이 선택한 조직으로 이동되었습니다.');
      showToast('현장 이관이 완료되었습니다.', 'success');
    } catch (err) {
      const apiError = extractApiError(err);
      setActionError(apiError.message);
    } finally {
      setIsAccepting(false);
    }
  };

  const handleReject = async () => {
    if (!token) return;

    setActionError('');
    setIsRejecting(true);
    try {
      await rejectTransfer(token);
      setIsDone(true);
      setDoneMessage('이관 요청을 거부했습니다.');
      showToast('이관 요청을 거부했습니다.', 'success');
    } catch (err) {
      const apiError = extractApiError(err);
      setActionError(apiError.message);
    } finally {
      setIsRejecting(false);
    }
  };

  const renderContent = () => {
    // 로딩 중
    if (isLoading) {
      return (
        <div className="flex flex-col items-center justify-center py-8">
          <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-brand-600" />
          <p className="mt-3 text-sm text-ink-500">이관 정보를 불러오는 중...</p>
        </div>
      );
    }

    // 에러
    if (loadError) {
      return (
        <div className="text-center py-6">
          <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center mx-auto">
            <svg className="w-6 h-6 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
          </div>
          <h3 className="mt-4 text-base font-semibold text-ink-900">이관 요청을 찾을 수 없습니다</h3>
          <p className="mt-2 text-sm text-ink-500">{loadError}</p>
          <p className="mt-1 text-xs text-ink-400">요청이 만료되었거나 이미 처리된 요청일 수 있습니다.</p>
          <Button variant="primary" onClick={onClose} className="mt-6">
            닫기
          </Button>
        </div>
      );
    }

    // 처리 완료
    if (isDone) {
      return (
        <div className="text-center py-6">
          <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center mx-auto">
            <svg className="w-6 h-6 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
          </div>
          <h3 className="mt-4 text-base font-semibold text-ink-900">완료</h3>
          <p className="mt-2 text-sm text-ink-600">{doneMessage}</p>
          <Button variant="primary" onClick={onClose} className="mt-6">
            닫기
          </Button>
        </div>
      );
    }

    // 요청자 본인
    if (isRequester) {
      return (
        <div className="text-center py-6">
          <div className="w-12 h-12 bg-orange-100 rounded-full flex items-center justify-center mx-auto">
            <svg className="w-6 h-6 text-orange-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
          </div>
          <h3 className="mt-4 text-base font-semibold text-ink-900">본인이 요청한 이관입니다</h3>
          <p className="mt-2 text-sm text-ink-500">
            이 이관 요청은 본인이 보낸 것입니다. 받는 사람이 수락해야 합니다.
          </p>
          <Button variant="primary" onClick={onClose} className="mt-6">
            닫기
          </Button>
        </div>
      );
    }

    // 메인 폼
    return (
      <div className="space-y-4">
        {/* 현장 정보 */}
        {transferInfo && (
          <Card surface="soft" elevation="none" className="space-y-2">
            {transferInfo.fromCompanyName && (
              <p className="text-xs text-ink-500">
                <span className="font-medium text-ink-700">"{transferInfo.fromCompanyName}"</span>에서 요청
              </p>
            )}
            <div>
              <p className="text-xs text-ink-500">현장명</p>
              <p className="text-sm font-semibold text-ink-900">{transferInfo.siteName}</p>
            </div>
            {transferInfo.siteAddress && (
              <div>
                <p className="text-xs text-ink-500">주소</p>
                <p className="text-sm text-ink-700">{transferInfo.siteAddress}</p>
              </div>
            )}
            <div className="flex gap-4 pt-1">
              <div>
                <p className="text-xs text-ink-500">점검</p>
                <p className="text-sm font-medium text-ink-900">{transferInfo.inspectionCount}건</p>
              </div>
              <div>
                <p className="text-xs text-ink-500">보고서</p>
                <p className="text-sm font-medium text-ink-900">{transferInfo.reportCount}건</p>
              </div>
            </div>
          </Card>
        )}

        {/* 이관 받을 조직 선택 */}
        <div>
          <label className="block text-sm font-medium text-ink-700 mb-1">
            이관 받을 조직
          </label>
          {ownerMemberships.length === 0 ? (
            <div className="bg-yellow-50 rounded-card-sm px-3 py-2 text-sm text-yellow-700">
              Owner 권한이 있는 조직이 없습니다. 이관을 수락하려면 먼저 조직을 생성하세요.
            </div>
          ) : (
            <select
              value={selectedOrgId}
              onChange={(e) => setSelectedOrgId(e.target.value)}
              className="w-full rounded-control border border-ink-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-transparent"
            >
              <option value="">조직 선택...</option>
              {ownerMemberships.map((m) => (
                <option key={m.companyId} value={m.companyId}>
                  {m.company.name}
                </option>
              ))}
            </select>
          )}
        </div>

        {/* 그룹 선택 — 그룹이 2개 이상일 때만 표시 */}
        {selectedOrgId && (
          <div>
            {isLoadingGroups ? (
              <div className="flex items-center gap-2 text-sm text-ink-500 py-2">
                <div className="animate-spin h-4 w-4 border-2 border-brand-500 border-t-transparent rounded-full" />
                그룹 목록 로딩 중...
              </div>
            ) : groups.length > 1 ? (
              <>
                <label className="block text-sm font-medium text-ink-700 mb-1">
                  이관 받을 그룹
                  <span className="ml-1 text-xs font-normal text-ink-400">(선택)</span>
                </label>
                <select
                  value={selectedGroupId}
                  onChange={(e) => setSelectedGroupId(e.target.value)}
                  className="w-full rounded-control border border-ink-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-transparent"
                >
                  <option value="">그룹 없음 (기본)</option>
                  {groups.map((g) => (
                    <option key={g.id} value={g.id}>
                      {g.name}
                      {g.isDefault ? ' (기본)' : ''}
                    </option>
                  ))}
                </select>
              </>
            ) : null}
          </div>
        )}

        {/* 안내 */}
        <p className="text-xs text-ink-400 bg-ink-50 rounded-card-sm px-3 py-2">
          수락 시 현장의 모든 점검 및 보고서가 선택한 조직으로 이동됩니다.
          이전 조직에서의 접근 권한은 사라집니다.
        </p>

        {/* 액션 에러 */}
        {actionError && (
          <p className="text-sm text-red-600 bg-red-50 rounded-card-sm px-3 py-2">{actionError}</p>
        )}

        {/* 버튼 */}
        <div className="flex gap-3 pt-1">
          <Button
            variant="secondary"
            onClick={handleReject}
            disabled={isRejecting || isAccepting}
            className="flex-1"
          >
            {isRejecting ? '처리 중...' : '거부'}
          </Button>
          <Button
            variant="primary"
            onClick={handleAccept}
            disabled={isAccepting || isRejecting || !selectedOrgId || ownerMemberships.length === 0}
            className="flex-1"
          >
            {isAccepting ? '수락 중...' : '수락'}
          </Button>
        </div>
      </div>
    );
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="현장 이관 요청"
      size="md"
      preventClose={isAccepting || isRejecting}
    >
      {renderContent()}
    </Modal>
  );
}
